import { useRef, useCallback, useState, useEffect } from 'react';
import { Send, Loader2, Paperclip, X, FileText, Image as ImageIcon, FileSpreadsheet, Mic, MicOff } from 'lucide-react';
import { useVoiceInput } from '@/hooks/useVoiceInput';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

// ── Types ──────────────────────────────────────────────────────────

export interface PendingFile {
  id: string;
  dataUri: string;
  name: string;
  mimeType: string;
  size: number;
}

export type FileCategory = 'image' | 'pdf' | 'spreadsheet' | 'audio' | 'other';

const ACCEPTED_TYPES: Record<FileCategory, string[]> = {
  image: ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/heic'],
  pdf: ['application/pdf'],
  spreadsheet: ['text/csv', 'text/tab-separated-values', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
  audio: ['audio/webm', 'audio/mp4', 'audio/mpeg', 'audio/wav', 'audio/ogg'],
  other: [],
};

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

export function getFileCategory(mimeType: string): FileCategory {
  for (const [cat, types] of Object.entries(ACCEPTED_TYPES)) {
    if (types.includes(mimeType)) return cat as FileCategory;
  }
  return 'other';
}

function getAllAcceptedMimeTypes(): string {
  return Object.values(ACCEPTED_TYPES).flat().join(',');
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Props ──────────────────────────────────────────────────────────

export interface ChatInputProps {
  input: string;
  onInputChange: (value: string) => void;
  onSubmit: (e?: React.FormEvent) => void;
  isLoading?: boolean;
  placeholder?: string;
  pendingFiles?: PendingFile[];
  onAddFiles?: (files: PendingFile[]) => void;
  onRemoveFile?: (id: string) => void;
  showFileUpload?: boolean;
  showVoiceInput?: boolean;
  className?: string;
  autoFocus?: boolean;
  onError?: (message: string) => void;
}

// ── Component ──────────────────────────────────────────────────────

export function ChatInput({
  input,
  onInputChange,
  onSubmit,
  isLoading = false,
  placeholder = 'Escriu un missatge...',
  pendingFiles = [],
  onAddFiles,
  onRemoveFile,
  showFileUpload = true,
  showVoiceInput = true,
  className,
  autoFocus = false,
  onError,
}: ChatInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const dragCounter = useRef(0);

  // ── Voice input ──

  const preVoiceInputRef = useRef('');

  const { isRecording, isTranscribing, toggleRecording, isSupported: isVoiceSupported } = useVoiceInput({
    language: 'ca',
    onTranscript: (text) => {
      // Replace the voice portion in real-time (keep pre-existing text)
      const base = preVoiceInputRef.current;
      const separator = base.trim() ? ' ' : '';
      onInputChange(base + separator + text);
    },
    onFinalTranscript: () => {
      // Recording finished — reset the base for next recording
      preVoiceInputRef.current = '';
      textareaRef.current?.focus();
    },
    onError: (msg) => onError?.(msg),
  });

  // Save input text when starting to record
  const handleToggleRecording = useCallback(() => {
    if (!isRecording) {
      preVoiceInputRef.current = input;
    }
    toggleRecording();
  }, [isRecording, input, toggleRecording]);

  // ── Auto-resize textarea ──

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 150)}px`;
  }, [input]);

  // ── Process raw File objects into PendingFile ──

  const showError = useCallback(
    (msg: string) => {
      if (onError) {
        onError(msg);
      }
    },
    [onError],
  );

  const processFiles = useCallback(
    (rawFiles: FileList | File[]) => {
      if (!onAddFiles) return;

      const filesToAdd: PendingFile[] = [];
      const validFiles = Array.from(rawFiles).filter((file) => {
        const category = getFileCategory(file.type);
        if (category === 'other') {
          showError(`Tipus de fitxer no suportat: ${file.type || file.name}`);
          return false;
        }
        if (file.size > MAX_FILE_SIZE) {
          showError(`Fitxer massa gran: ${file.name} (màx. ${formatFileSize(MAX_FILE_SIZE)})`);
          return false;
        }
        return true;
      });

      if (validFiles.length === 0) return;

      validFiles.forEach((file) => {
        const reader = new FileReader();
        reader.onload = (ev) => {
          const dataUri = ev.target?.result as string;
          if (dataUri) {
            filesToAdd.push({
              id: crypto.randomUUID(),
              dataUri,
              name: file.name,
              mimeType: file.type,
              size: file.size,
            });
            if (filesToAdd.length === validFiles.length) {
              onAddFiles(filesToAdd);
            }
          }
        };
        reader.readAsDataURL(file);
      });
    },
    [onAddFiles, showError],
  );

  // ── File input handler ──

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) processFiles(e.target.files);
      e.target.value = '';
    },
    [processFiles],
  );

  // ── Drag & Drop handlers ──

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items?.length) setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      dragCounter.current = 0;
      if (e.dataTransfer.files?.length) {
        processFiles(e.dataTransfer.files);
      }
    },
    [processFiles],
  );

  // ── Keyboard handler (Enter/Shift+Enter) ──

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
        e.preventDefault();
        if (canSubmit) onSubmit();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [input, pendingFiles, isLoading, onSubmit],
  );

  // ── Helpers ──

  const hasFiles = pendingFiles.length > 0;
  const canSubmit = (input.trim() || hasFiles) && !isLoading;

  const getFileIcon = (mimeType: string) => {
    const cat = getFileCategory(mimeType);
    switch (cat) {
      case 'image': return <ImageIcon className="h-4 w-4" />;
      case 'pdf': return <FileText className="h-4 w-4 text-red-500" />;
      case 'spreadsheet': return <FileSpreadsheet className="h-4 w-4 text-green-600" />;
      case 'audio': return <Mic className="h-4 w-4 text-blue-500" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const getPlaceholder = () => {
    if (!hasFiles) return placeholder;
    const cats = [...new Set(pendingFiles.map(f => getFileCategory(f.mimeType)))];
    if (cats.length === 1 && cats[0] === 'image') return 'Descriu la imatge...';
    if (cats.length === 1 && cats[0] === 'pdf') return 'Què vols fer amb aquest PDF?';
    return 'Descriu els fitxers adjunts...';
  };

  // ── Render ──

  return (
    <div
      className={cn('relative', className)}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drag overlay */}
      {isDragging && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl border-2 border-dashed border-primary bg-primary/5 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-1 text-primary">
            <Paperclip className="h-6 w-6" />
            <span className="text-sm font-medium">Deixa anar els fitxers aquí</span>
            <span className="text-xs text-muted-foreground">Imatges, PDF, CSV...</span>
          </div>
        </div>
      )}

      {/* Hidden file input */}
      {showFileUpload && onAddFiles && (
        <input
          ref={fileInputRef}
          type="file"
          accept={getAllAcceptedMimeTypes()}
          multiple
          className="hidden"
          onChange={handleFileSelect}
        />
      )}

      {/* Composer container */}
      <div
        className={cn(
          'rounded-2xl border bg-background shadow-sm transition-shadow',
          isFocused && 'ring-2 ring-ring/50 border-ring',
          isDragging && 'border-primary',
        )}
      >
        {/* File Previews — inside container */}
        {hasFiles && (
          <div className="flex gap-2 flex-wrap px-3 pt-3">
            {pendingFiles.map((file) => {
              const isImage = getFileCategory(file.mimeType) === 'image';
              return (
                <div key={file.id} className="relative">
                  {isImage ? (
                    <img
                      src={file.dataUri}
                      alt={file.name}
                      className="h-16 w-16 sm:h-14 sm:w-14 object-cover rounded-lg border"
                    />
                  ) : (
                    <div className="h-16 w-28 sm:h-14 sm:w-24 flex flex-col items-center justify-center gap-1 rounded-lg border bg-muted/50 px-2">
                      {getFileIcon(file.mimeType)}
                      <span className="text-[10px] text-muted-foreground truncate w-full text-center" title={file.name}>
                        {file.name}
                      </span>
                      <span className="text-[9px] text-muted-foreground">
                        {formatFileSize(file.size)}
                      </span>
                    </div>
                  )}
                  {/* Delete button — ALWAYS visible, touch-friendly */}
                  {onRemoveFile && (
                    <button
                      type="button"
                      onClick={() => onRemoveFile(file.id)}
                      className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-0.5 shadow-sm min-w-[24px] min-h-[24px] flex items-center justify-center active:scale-95 transition-transform"
                      aria-label={`Eliminar ${file.name}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Input row: attach + textarea + send */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) onSubmit();
          }}
          className="flex items-end gap-1 p-2"
        >
          {/* Attach file button */}
          {showFileUpload && onAddFiles && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={isLoading}
                    onClick={() => fileInputRef.current?.click()}
                    className="shrink-0 h-9 w-9 rounded-full text-muted-foreground hover:text-foreground"
                  >
                    {hasFiles ? (
                      <div className="relative">
                        <Paperclip className="h-5 w-5 text-primary" />
                        <span className="absolute -top-1.5 -right-1.5 bg-primary text-primary-foreground text-[9px] rounded-full h-4 w-4 flex items-center justify-center font-medium">
                          {pendingFiles.length}
                        </span>
                      </div>
                    ) : (
                      <Paperclip className="h-5 w-5" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Adjuntar fitxer (imatge, PDF, CSV...)</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {/* Auto-resize textarea */}
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder={getPlaceholder()}
            disabled={isLoading}
            autoFocus={autoFocus}
            rows={1}
            className={cn(
              'flex-1 resize-none bg-transparent border-0 outline-none',
              'text-base md:text-sm',
              'placeholder:text-muted-foreground',
              'disabled:cursor-not-allowed disabled:opacity-50',
              'py-2 px-1',
              'max-h-[150px] overflow-y-auto',
              'scrollbar-thin scrollbar-thumb-muted-foreground/20',
            )}
          />

          {/* Voice input button */}
          {showVoiceInput && isVoiceSupported && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={isLoading}
                    onClick={handleToggleRecording}
                    className={cn(
                      'shrink-0 h-9 w-9 rounded-full transition-all',
                      isRecording
                        ? 'bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 animate-pulse'
                        : isTranscribing
                          ? 'text-blue-500'
                          : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {isTranscribing ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : isRecording ? (
                      <MicOff className="h-5 w-5" />
                    ) : (
                      <Mic className="h-5 w-5" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{isRecording ? 'Parar gravació' : isTranscribing ? 'Transcrivint...' : 'Parlar (veu a text)'}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {/* Send button — visible only when can submit */}
          <div
            className={cn(
              'shrink-0 transition-all duration-200',
              canSubmit ? 'opacity-100 scale-100' : 'opacity-0 scale-75 pointer-events-none',
            )}
          >
            <Button
              type="submit"
              size="icon"
              disabled={!canSubmit}
              className="h-9 w-9 rounded-full"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
